import React, { useState, useEffect } from 'react';
import { Power, PowerOff, Save, RefreshCw, CheckCircle, XCircle, Info } from 'lucide-react';
import { getSubscriptionConfig, updateSubscriptionConfig } from '../config/subscriptionConfig';
import { usePermissions } from '../hooks/usePermissions';
import { getAuth } from 'firebase/auth';

const SubscriptionModuleConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;
  const { hasPermission } = usePermissions({ userId: user?.uid });

  // Vérifier la permission de gérer les paiements
  const canManagePayments = hasPermission({ resource: 'payments', action: 'manage' });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const currentConfig = await getSubscriptionConfig();
      setConfig(currentConfig);
    } catch (err) {
      console.error('Erreur lors du chargement de la configuration:', err);
      setError('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async () => {
    if (!canManagePayments) {
      setError('Vous n\'avez pas la permission de modifier cette configuration');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const newValue = !config.SUBSCRIPTION_MODULE_ENABLED;
      await updateSubscriptionConfig({
        SUBSCRIPTION_MODULE_ENABLED: newValue
      });

      setSuccessMessage(
        newValue
          ? '✅ Module d\'abonnement activé avec succès! Les restrictions d\'accès sont maintenant appliquées.'
          : '✅ Module d\'abonnement désactivé avec succès! Tous les utilisateurs ont accès complet.'
      );

      // Recharger la configuration
      await loadConfig();

      // Effacer le message après 5 secondes
      setTimeout(() => setSuccessMessage(null), 5000);

      // Rafraîchir la page après 2 secondes pour appliquer les changements
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la mise à jour de la configuration');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (!canManagePayments) {
    return (
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex items-center text-red-600 dark:text-red-400">
          <XCircle className="w-5 h-5 mr-2" />
          <span>Vous n'avez pas la permission d'accéder à cette configuration.</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex items-center text-red-600 dark:text-red-400">
          <XCircle className="w-5 h-5 mr-2" />
          <span>Impossible de charger la configuration</span>
        </div>
      </div>
    );
  }

  const isEnabled = config.SUBSCRIPTION_MODULE_ENABLED;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* En-tête */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              {isEnabled ? (
                <Power className="w-7 h-7 mr-3 text-green-500" />
              ) : (
                <PowerOff className="w-7 h-7 mr-3 text-gray-400" />
              )}
              Module d'abonnement
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Gérez les restrictions d'accès basées sur les abonnements
            </p>
          </div>
          <div className={`px-4 py-2 rounded-full font-medium ${
            isEnabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {isEnabled ? 'Activé' : 'Désactivé'}
          </div>
        </div>
      </div>

      {/* Statut actuel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Statut actuel
        </h3>

        {isEnabled ? (
          <div className="space-y-4">
            <div className="flex items-start p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900 dark:text-green-300">Module activé</p>
                <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                  Les restrictions d'accès sont appliquées. Les utilisateurs sans abonnement premium n'ont accès qu'au dashboard et à la page d'abonnement.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Règles actives:</h4>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span><strong>Premium/Offert:</strong> Accès complet selon les permissions du rôle</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">⚠</span>
                  <span><strong>Gratuit/Expiré/Aucun:</strong> Accès limité au dashboard et abonnement uniquement</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
              <Info className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Module désactivé</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Tous les utilisateurs ont accès à toutes les fonctionnalités selon leurs permissions de rôle uniquement. Les abonnements ne limitent pas l'accès.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Actions
        </h3>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleToggleModule}
            disabled={saving}
            className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-colors ${
              isEnabled
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : isEnabled ? (
              <>
                <PowerOff className="w-5 h-5 mr-2" />
                Désactiver le module
              </>
            ) : (
              <>
                <Power className="w-5 h-5 mr-2" />
                Activer le module
              </>
            )}
          </button>

          <button
            onClick={loadConfig}
            disabled={saving}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Avertissement */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-800 dark:text-yellow-300">
            <p className="font-medium mb-1">Important:</p>
            <p>
              La modification de ce paramètre prendra effet immédiatement pour tous les utilisateurs.
              La page se rechargera automatiquement après l'enregistrement pour appliquer les changements.
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-100 dark:bg-green-900 border-l-4 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300 p-4 rounded shadow-lg max-w-md z-50">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900 border-l-4 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300 p-4 rounded shadow-lg max-w-md z-50">
          <div className="flex items-start">
            <XCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionModuleConfig;
